import { useRef, useState } from "react";
import type { ProjectBrief } from "../../contracts/types";
import { extractManyPdfs } from "../pdf/extractText";

// Upload one or more contract drawings / documents; extract their text in the
// browser, send it to /api/extract, and merge the returned fields into the brief.
export function PdfImport({ onMerge }: { onMerge: (patch: Partial<ProjectBrief>) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleFiles(files: FileList | null) {
    if (!files || !files.length) return;
    setBusy(true);
    setErr(null);
    setStatus(`Reading ${files.length} file(s)…`);
    try {
      const results = await extractManyPdfs(Array.from(files));
      const totalChars = results.reduce((s, r) => s + r.chars, 0);
      const combined = results.map((r) => r.text).join("\n\n");
      setStatus(`Extracted ${totalChars.toLocaleString()} chars from ${results.length} file(s). Asking AI to structure it…`);
      if (!combined.trim()) {
        setErr("No selectable text found (the PDFs may be scanned images). Try a vector PDF, or enter details manually.");
        setStatus(null);
        return;
      }
      const res = await fetch("/api/extract", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: combined }),
      });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const data = (await res.json()) as { brief?: Partial<ProjectBrief> };
      if (data.brief) {
        onMerge(data.brief);
        const keys = Object.keys(data.brief);
        const dwg = Array.isArray(data.brief.drawings) ? data.brief.drawings.length : 0;
        setStatus(`Imported ${keys.length} field group(s)${dwg ? `, ${dwg} drawing(s)` : ""}. Review below.`);
      } else {
        setStatus("No fields returned.");
      }
    } catch (e: any) {
      setErr(`Import failed (${String(e.message).slice(0, 120)}). AI extraction needs the Worker + ANTHROPIC_API_KEY.`);
      setStatus(null);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="pdf-import">
      <div className="hint">Upload contract drawings or documents (PDF). Text is read in your browser, then AI extracts the project details, description and drawing register.</div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" className="dark" onClick={() => inputRef.current?.click()} disabled={busy}>
          {busy ? <span className="spinner" /> : "⤒ Import from PDF"}
        </button>
        <input ref={inputRef} type="file" accept="application/pdf" multiple style={{ display: "none" }} onChange={(e) => handleFiles(e.target.files)} />
        {status && <span className="hint" style={{ margin: 0 }}>{status}</span>}
      </div>
      {err && <div className="hint" style={{ color: "#a3402f", marginTop: 6 }}>{err}</div>}
    </div>
  );
}
